// File: frontend/src/App.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SchoolProvider } from './context/SchoolContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import AdminDashboard from './pages/dashboards/AdminDashboard';
import PrincipalDashboard from './pages/dashboards/PrincipalDashboard';
import TeacherDashboard from './pages/dashboards/TeacherDashboard';
import StudentDashboard from './pages/dashboards/StudentDashboard';
import AccountantDashboard from './pages/dashboards/AccountantDashboard';
import GenericDashboard from './pages/dashboards/GenericDashboard';
import MyProfile from './pages/profile/MyProfile';
import AdmissionList from './pages/admissions/AdmissionList';
import AdmissionForm from './pages/admissions/AdmissionForm';
import StudentList from './pages/students/StudentList';
import StudentForm from './pages/students/StudentForm';
import StudentDetail from './pages/students/StudentDetail';
import StudentNavigator from './pages/students/StudentNavigator';
import MarkAttendance from './pages/attendance/MarkAttendance';
import AttendanceReport from './pages/attendance/AttendanceReport';
import AttendanceSummary from './pages/attendance/AttendanceSummary';
import FeeDashboard from './pages/fees/FeeDashboard';
import FeeCollection from './pages/fees/FeeCollection';
import FeeStructure from './pages/fees/FeeStructure';
import Defaulters from './pages/fees/Defaulters';
import ExamList from './pages/exams/ExamList';
import EnterResults from './pages/exams/EnterResults';
import ResultCard from './pages/exams/ResultCard';
import ResultList from './pages/exams/ResultList';
import StaffList from './pages/staff/StaffList';
import StaffForm from './pages/staff/StaffForm';
import StaffDetail from './pages/staff/StaffDetail';
import Payroll from './pages/staff/Payroll';
import SalarySlip from './pages/staff/SalarySlip';
import ParentDashboard from './pages/parent/ParentDashboard';
import ChildAttendance from './pages/parent/ChildAttendance';
import ChildFees from './pages/parent/ChildFees';
import ChildResults from './pages/parent/ChildResults';
import Notices from './pages/parent/Notices';
import SchoolSettings from './pages/settings/SchoolSettings';
import ReportsDashboard from './pages/reports/ReportsDashboard';
import StudentReport from './pages/reports/StudentReport';
import AttendanceReportPage from './pages/reports/AttendanceReport';
import FeeReportPage from './pages/reports/FeeReport';
import ExamReportPage from './pages/reports/ExamReport';
import StaffReportPage from './pages/reports/StaffReport';
import TimetableManagement from './pages/timetable/TimetableManagement';
import MyTimetable from './pages/timetable/MyTimetable';
import IDCardGenerator from './pages/idcards/IDCardGenerator';
import AITestGenerator from './pages/aitests/AITestGenerator';
import SavedTests from './pages/aitests/SavedTests';
import BrandingSettings from './pages/branding/BrandingSettings';
import SuperAdminHub from './pages/admin/SuperAdminHub';
import UserManagement from './pages/settings/UserManagement';
import Unauthorized from './pages/Unauthorized';

function AppHeader({ onInstallClick }) {
  const { user } = useAuth();

  const getDashboardPath = (userRole) => {
    const normRole = userRole?.toLowerCase();
    switch (normRole) {
      case 'super_admin':
      case 'admin':
        return '/dashboard/admin';
      case 'principal':
        return '/dashboard/principal';
      case 'accountant':
        return '/dashboard/accountant';
      case 'teacher':
        return '/dashboard/teacher';
      case 'librarian':
        return '/dashboard/librarian';
      case 'parent':
        return '/parent/dashboard';
      case 'student':
        return '/dashboard/student';
      default:
        return '/dashboard/admin';
    }
  };

  const dashboardPath = user ? getDashboardPath(user.role) : '/login';

  return (
    <header className="bg-slate-900 text-white px-4 py-2.5 shadow-md flex justify-between items-center sticky top-0 z-50 print:hidden">
      <div className="flex items-center space-x-3">
        <a href={dashboardPath} className="flex items-center space-x-2 group">
          <span className="font-bold text-base tracking-wide text-blue-400 group-hover:text-blue-300 transition">SchoolMS</span>
          <span className="text-xs text-slate-400 hidden sm:inline">| School Management System</span>
        </a>
      </div>
      <div className="flex items-center space-x-2.5">
        {user && (
          <a
            href={dashboardPath}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs sm:text-sm px-3 py-1.5 rounded-lg shadow transition flex items-center space-x-1.5 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>Main Dashboard</span>
          </a>
        )}
        <a
          href="/profile"
          className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-slate-700 transition flex items-center space-x-1.5 cursor-pointer"
        >
          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="hidden sm:inline">My Profile</span>
        </a>
        <button
          onClick={onInstallClick}
          className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-slate-700 transition flex items-center space-x-1.5 cursor-pointer"
        >
          <span>Install</span>
        </button>
      </div>
    </header>
  );
}

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role?.toLowerCase();
  switch (role) {
    case 'super_admin':
    case 'admin':
    case 'principal':
      return <Navigate to="/dashboard/admin" replace />;
    case 'accountant':
      return <Navigate to="/dashboard/accountant" replace />;
    case 'teacher':
      return <Navigate to="/dashboard/teacher" replace />;
    case 'parent':
      return <Navigate to="/parent/dashboard" replace />;
    case 'student':
      return <Navigate to="/dashboard/student" replace />;
    default:
      return <Navigate to="/dashboard/admin" replace />;
  }
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the PWA installation');
        }
        setDeferredPrompt(null);
      });
    } else {
      alert('To install SchoolMS on your device: open your browser menu (⋮ or Share icon) and select "Add to Home Screen" or "Install App".');
    }
  };

  // Main application routing and provider setup
  return (
    <AuthProvider>
      <SchoolProvider>
        <Router>
          <Routes>
          {/* Smart Root Route: Redirects logged-in users to their role dashboard */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Self-Service Profile Route */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant', 'librarian', 'parent', 'student']}>
                <MyProfile />
              </ProtectedRoute>
            }
          />

          {/* Protected Fee Module Routes */}
          <Route
            path="/fees"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <FeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/collect"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <FeeCollection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/structure"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <FeeStructure />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fees/defaulters"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <Defaulters />
              </ProtectedRoute>
            }
          />

          {/* Protected Exams & Results Module Routes */}
          <Route
            path="/exams"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <ExamList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/results/:examId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <ResultList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/enter/:examId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <EnterResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exams/reportcard/:studentId/:examId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'parent', 'student']}>
                <ResultCard />
              </ProtectedRoute>
            }
          />

          {/* Protected AI Test Module Routes */}
          <Route
            path="/ai-tests"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <AITestGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-tests/saved"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <SavedTests />
              </ProtectedRoute>
            }
          />

          {/* Protected Timetable Module Routes */}
          <Route
            path="/timetable"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <TimetableManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-timetable"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'student', 'parent']}>
                <MyTimetable />
              </ProtectedRoute>
            }
          />

          {/* Protected Staff & HR Module Routes */}
          <Route
            path="/staff"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <StaffList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <StaffForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/payroll"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <Payroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/payroll/:staffId/slip"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <SalarySlip />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/:id"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <StaffDetail />
              </ProtectedRoute>
            }
          />

          {/* Protected Parent & Student Portal Routes */}
          <Route
            path="/parent/dashboard"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent', 'student']}>
                <ParentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent/attendance"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent', 'student']}>
                <ChildAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent/fees"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent', 'student']}>
                <ChildFees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent/results"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent', 'student']}>
                <ChildResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent/notices"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent', 'student']}>
                <Notices />
              </ProtectedRoute>
            }
          />

          {/* Master Super Admin Hub & Redirects */}
          <Route
            path="/admin/hub"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminHub />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/super-settings" element={<Navigate to="/admin/hub" replace />} />
          <Route path="/settings/schools" element={<Navigate to="/admin/hub?tab=schools" replace />} />
          <Route path="/settings/school-settings" element={<Navigate to="/admin/hub?tab=branding" replace />} />
          <Route path="/branding" element={<Navigate to="/admin/hub?tab=branding" replace />} />

          {/* Protected White Label & User Management Settings Routes */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <SchoolSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Protected Reports & Analytics Module Routes */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant', 'teacher']}>
                <ReportsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/students"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <StudentReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/attendance"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <AttendanceReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/fees"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <FeeReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/exams"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <ExamReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/staff"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <StaffReportPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Attendance Module Routes */}
          <Route
            path="/attendance/mark"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <MarkAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/report"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant']}>
                <AttendanceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/summary"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant']}>
                <AttendanceSummary />
              </ProtectedRoute>
            }
          />

          {/* Protected Student Module Routes */}
          <Route
            path="/students"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant']}>
                <StudentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <StudentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/edit/:id"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <StudentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id/edit"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <StudentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/navigator"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant']}>
                <StudentNavigator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher', 'accountant']}>
                <StudentDetail />
              </ProtectedRoute>
            }
          />

          {/* Protected Admissions Module Routes */}
          <Route
            path="/admissions"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <AdmissionList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions/new"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <AdmissionForm />
              </ProtectedRoute>
            }
          />

          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/principal"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <PrincipalDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/accountant"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'accountant']}>
                <AccountantDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/teacher"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'teacher']}>
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/librarian"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'librarian']}>
                <GenericDashboard title="Librarian Dashboard" description="Track book catalog, manage borrowings, returns, and library inventory." />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/parent"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'parent']}>
                <ParentDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/student"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal', 'student']}>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/idcards"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'principal']}>
                <IDCardGenerator />
              </ProtectedRoute>
            }
          />

          <Route
            path="/branding"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
                <BrandingSettings />
              </ProtectedRoute>
            }
          />

          {/* Fallback Catch-All Route */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </SchoolProvider>
  </AuthProvider>
  );
}

export default App;
